import { useForm, FormProvider, type SubmitHandler } from 'react-hook-form';
import {
  Box,
  Button,
  DetailsDisclosure,
  Link,
  Paragraph,
} from '@alto-avios/alto-ui';
import { Hero } from '@/components/Hero';
import { PersonalDetails } from '@/components/PersonalDetails';
import { LoginDetails } from '@/components/LoginDetails';
import { TermsAndConditions } from '@/components/TermsAndConditions';
import { useTranslation } from '@/utils/useTranslation';
import { useGlobalContext } from '../GlobalProvider';
import { Programmes, View } from '@/utils/constants';
import {
  Children,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/router';
import type { Enrolment, ProgrammeId } from '@/common/types';
import { AlreadyMemberSection } from '@/components/AlreadyMemberSection';
import axios from 'axios';

type Props = {
  programmeId: ProgrammeId;
};

type Inputs = {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string | null;
  countryOfResidence: string;
  addressLine: string;
  city: string;
  postCode: string;
  email: string;
  confirmEmail: string;
  password: string;
  marketingOptOut: boolean;
  sendNews: boolean;
  shareInformation: boolean;
  acceptTerms: boolean;
};

const EnrolmentForm = (props: Props) => {
  const formMethods = useForm<Inputs>({
    defaultValues: {
      middleName: '',
      dateOfBirth: null,
      marketingOptOut: false,
      sendNews: false,
      shareInformation: false,
      acceptTerms: false,
    },
  });
  const { setName, setCurrentView } = useGlobalContext();
  const { t } = useTranslation('form');
  const { t: tTerms, tRich: tTermsRich } = useTranslation('termsAndConditions');
  const router = useRouter();
  const [isPending, setIsPending] = useState(false);
  const isMountedRef = useRef(true);
  const activeRequestControllerRef = useRef<AbortController | null>(null);

  const isRequestCanceled = (error: unknown): boolean => {
    return (
      axios.isCancel(error) ||
      (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')
    );
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      activeRequestControllerRef.current?.abort();
    };
  }, []);

  const alreadyAMemberLink = useMemo(() => {
    return process.env.NEXT_PUBLIC_ENVIRONMENT === 'prd'
      ? 'https://www.avios.com/manage-avios/activity'
      : 'https://stg.avios.com/manage-avios/activity';
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async (data: Inputs) => {
    setIsPending(true);
    const requestController = new AbortController();
    activeRequestControllerRef.current = requestController;

    try {
      const {
        firstName,
        middleName,
        lastName,
        dateOfBirth,
        countryOfResidence,
        addressLine,
        city,
        postCode,
        email,
        password,
        marketingOptOut,
        sendNews,
        shareInformation,
        acceptTerms,
      } = data;

      const campaignCode = (router?.query?.campaignCode as string) ?? undefined;

      const enrolmentData: Enrolment = {
        emailAddress: email,
        password,
        firstName,
        ...(middleName ? { middleName } : {}),
        familyName: lastName,
        dateOfBirth: new Date(dateOfBirth || '').toISOString(),
        address: {
          addressLine,
          city,
          postCode,
        },
        countryOfResidence,
        receiveNews:
          props.programmeId === Programmes.BAEC ? !marketingOptOut : sendNews,
        termsAndConditions: acceptTerms,
        shareInformation:
          props.programmeId === Programmes.BAEC ? false : shareInformation,
        campaignCode,
      };

      const response = await axios.post(
        `/sign-up/api/${props.programmeId}/enrolment`,
        enrolmentData,
        { signal: requestController.signal },
      );

      if (response.status === 200) {
        setName?.(firstName);
        setCurrentView?.(View.SUCCESS);
      } else {
        setCurrentView?.(View.ERROR);
      }
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      setCurrentView?.(View.ERROR);
    } finally {
      if (activeRequestControllerRef.current === requestController) {
        activeRequestControllerRef.current = null;
      }

      if (isMountedRef.current) {
        setIsPending(false);
      }
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      backgroundColor="layer1"
      paddingX="sm"
      paddingY="3xl"
    >
      <Box
        display="flex"
        flexDirection="column"
        backgroundColor="base"
        borderRadius="lg"
        maxWidth="600px"
        width="full"
        borderWidth="xs"
        borderStyle="solid"
        borderColour="tertiary"
      >
        <Hero />
        <AlreadyMemberSection link={alreadyAMemberLink} />
        <FormProvider {...formMethods}>
          {/* react-hooks/refs: false positive with react-hook-form's handleSubmit wrapper */}
          {/* eslint-disable-next-line react-hooks/refs */}
          <form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
            <Box
              display="flex"
              flexDirection="column"
              gap="2xl"
              paddingY={{ base: '2xl', sm: '3xl' }}
              paddingX={{ base: 'sm', sm: '2xl' }}
            >
              <PersonalDetails />

              <LoginDetails />

              <Box display="flex" flexDirection="column" gap="xs">
                <TermsAndConditions />

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flexStart"
                  gap="lg"
                >
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth={false}
                    isLoading={isPending}
                    isDisabled={isPending}
                  >
                    {t('createAccount')}
                  </Button>

                  {props.programmeId === Programmes.BAEC && (
                    <DetailsDisclosure>
                      <DetailsDisclosure.Summary headingLevel="h3">
                        {tTerms('learnMore.summary')}
                      </DetailsDisclosure.Summary>
                      <DetailsDisclosure.Details>
                        <Paragraph size="sm">
                          {Children.toArray(
                            tTermsRich('learnMore.description', {
                              privacyLink: (chunks: ReactNode) => (
                                <Link
                                  key="privacy-policy-link"
                                  href={tTerms('links.privacy_baec')}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  iconEndProps={false}
                                >
                                  {chunks}
                                </Link>
                              ),
                            }),
                          )}
                        </Paragraph>
                      </DetailsDisclosure.Details>
                    </DetailsDisclosure>
                  )}
                </Box>
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default EnrolmentForm;
